import { useCallback, useEffect, useRef } from 'react';

type UseSafePaginationParams = {
  currentPageItemsCount: number | undefined;
  isFetching: boolean;
  onPageValidAfterMutation?: () => Promise<unknown>;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  totalItemsCount: number | undefined;
};

export const useSafePagination = ({
  currentPageItemsCount,
  isFetching,
  onPageValidAfterMutation,
  page,
  pageSize,
  setPage,
  totalItemsCount,
}: UseSafePaginationParams) => {
  const lastAdjustedPageRef = useRef<null | number>(null);

  const validatePageAfterMutation = useCallback(async () => {
    if (typeof totalItemsCount !== 'number' || typeof currentPageItemsCount !== 'number') {
      return;
    }

    const lastPage = Math.max(1, Math.ceil(totalItemsCount / pageSize));
    const isLastItemOnCurrentPage = currentPageItemsCount === 1;
    const shouldGoToPreviousPage = isLastItemOnCurrentPage && page > 1;

    if (shouldGoToPreviousPage) {
      setPage(page - 1);
    } else if (page > lastPage) {
      setPage(lastPage);
    } else {
      await onPageValidAfterMutation?.();
    }
  }, [currentPageItemsCount, onPageValidAfterMutation, page, pageSize, setPage, totalItemsCount]);

  useEffect(() => {
    if (isFetching) return;
    if (typeof totalItemsCount !== 'number') return;
    if (typeof currentPageItemsCount !== 'number') return;

    const lastPage = Math.max(1, Math.ceil(totalItemsCount / pageSize));

    if (page > lastPage && lastAdjustedPageRef.current !== page) {
      lastAdjustedPageRef.current = page;
      setPage(lastPage);
    }
  }, [currentPageItemsCount, isFetching, page, pageSize, setPage, totalItemsCount]);

  return { validatePageAfterMutation };
};
